import {
	IMilkingProcessDeviationDetail,
	IMilkingRoutineEvent,
	IMilkingUnitOverlay,
	IMilkingUnitOverlayPoint,
	MilkingProcessDeviationType,
} from 'models/MilkingProcess'
import moment from 'moment'
import React, { useId, useLayoutEffect, useRef, useState } from 'react'
import { COLORS } from 'styles/colors'

// Бэк присылает координаты в пикселях кадра без его размера, считаем кадр 1920x1080
const FRAME_WIDTH = 1920
const FRAME_HEIGHT = 1080
// Плеер Flussonic рисует видео над своей панелью управления, а не под ней
const IFRAME_BORDER_PX = 2
const PLAYER_CONTROLS_HEIGHT_PX = 100
const FONT_SIZE_RATIO = 0.048
const LINE_HEIGHT_RATIO = 1.6
const APPROX_CHAR_WIDTH_RATIO = 0.56

type TextSide = 'left' | 'right'

const CONNECTING_DEVIATION_TYPES: MilkingProcessDeviationType[] = [
	'issue_early_connecting',
	'issue_later_connecting',
]

interface MilkingUnitOverlayProps {
	overlay: IMilkingUnitOverlay
	events?: IMilkingRoutineEvent[] | null
	deviation?: IMilkingProcessDeviationDetail | null
}

const formatEvent = (
	event: IMilkingRoutineEvent,
	seconds: number | null | undefined,
	omitTime = false,
) => {
	if (event.status === 'skipped' || omitTime) {
		return seconds != null ? `${event.name} (${seconds} сек)` : event.name
	}
	const label =
		event.timestamp == null
			? event.name
			: `${event.name}: ${moment(event.timestamp).format('HH:mm:ss')}`
	return seconds != null ? `${label} (${seconds} сек)` : label
}

export const MilkingUnitOverlay: React.FC<MilkingUnitOverlayProps> = ({
	overlay,
	events,
	deviation,
}) => {
	const { rect, text } = overlay
	const rows = events ?? []
	const highlightConnecting =
		deviation?.type != null &&
		CONNECTING_DEVIATION_TYPES.includes(deviation.type)

	const eventSeconds = (event: IMilkingRoutineEvent) => {
		if (!highlightConnecting || event.type !== 'connecting') {
			return event.status === 'skipped' ? event.length_seconds : null
		}
		return deviation?.length_seconds ?? event.length_seconds
	}

	const isConnectingProblem = (event: IMilkingRoutineEvent) =>
		highlightConnecting && event.type === 'connecting'

	const fontSize = FRAME_HEIGHT * FONT_SIZE_RATIO
	const lineHeight = fontSize * LINE_HEIGHT_RATIO
	const iconRadius = fontSize * 0.45
	const iconGap = fontSize * 0.35
	const textGap = fontSize * 0.6
	const strokeWidth = FRAME_HEIGHT * 0.007
	const frameRadius = FRAME_HEIGHT * 0.016
	const labelPadX = fontSize * 0.9
	const labelPadY = fontSize * 0.75

	const textRefs = useRef<(SVGTextElement | null)[]>([])
	const overlayRef = useRef<HTMLDivElement>(null)
	const textShadowId = `milking-event-shadow-${useId().replace(/:/g, '')}`
	const [textWidths, setTextWidths] = useState<number[]>([])
	const [viewport, setViewport] = useState({ width: 0, height: 0 })

	useLayoutEffect(() => {
		setTextWidths(
			textRefs.current
				.slice(0, events?.length ?? 0)
				.map(node => node?.getComputedTextLength() ?? 0),
		)
	}, [events, fontSize, highlightConnecting, deviation?.length_seconds])

	useLayoutEffect(() => {
		const node = overlayRef.current
		if (!node) return
		const update = () => {
			const { width, height } = node.getBoundingClientRect()
			setViewport({ width, height })
		}
		update()
		const observer = new ResizeObserver(update)
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	const getTextWidth = (index: number) =>
		textWidths[index] ??
		formatEvent(
			rows[index],
			eventSeconds(rows[index]),
			isConnectingProblem(rows[index]),
		).length *
			fontSize *
			APPROX_CHAR_WIDTH_RATIO

	const columnWidth =
		Math.max(0, ...rows.map((_, index) => getTextWidth(index))) +
		iconGap +
		iconRadius * 2

	const resolveTextPlacement = (): {
		side: TextSide
		anchor: IMilkingUnitOverlayPoint
	} => {
		if (text) {
			const side: TextSide =
				text.x >= rect.x + rect.width / 2 ? 'right' : 'left'
			return { side, anchor: text }
		}
		const top = rect.y + fontSize
		const rightX = rect.x + rect.width + textGap
		if (rightX + columnWidth <= FRAME_WIDTH || rect.x - textGap < columnWidth) {
			return { side: 'right', anchor: { x: rightX, y: top } }
		}
		return { side: 'left', anchor: { x: rect.x - textGap, y: top } }
	}

	const { side, anchor: rawAnchor } = resolveTextPlacement()
	const columnSpan = Math.max(0, rows.length - 1) * lineHeight
	const minAnchorY = fontSize
	const maxAnchorY = FRAME_HEIGHT - fontSize * 0.3 - columnSpan
	const anchor = {
		...rawAnchor,
		y:
			maxAnchorY < minAnchorY
				? minAnchorY
				: Math.min(Math.max(rawAnchor.y, minAnchorY), maxAnchorY),
	}

	const rowLayouts = rows.map((event, index) => {
		const y = anchor.y + index * lineHeight
		const iconCx =
			side === 'right' ? anchor.x + iconRadius : anchor.x - iconRadius
		const textX =
			side === 'right'
				? iconCx + iconRadius + iconGap
				: iconCx - iconRadius - iconGap
		const textWidth = getTextWidth(index)
		const textLeft = side === 'right' ? textX : textX - textWidth
		const textRight = textLeft + textWidth
		return {
			event,
			y,
			iconCx,
			textX,
			left: Math.min(textLeft, iconCx - iconRadius),
			right: Math.max(textRight, iconCx + iconRadius),
		}
	})
	const labelBackdrop = (() => {
		if (rowLayouts.length === 0) return null
		const backdrop = {
			x: Math.min(...rowLayouts.map(row => row.left)) - labelPadX,
			y: rowLayouts[0].y - fontSize * 0.9 - labelPadY,
			width:
				Math.max(...rowLayouts.map(row => row.right)) -
				Math.min(...rowLayouts.map(row => row.left)) +
				labelPadX * 2,
			height:
				rowLayouts[rowLayouts.length - 1].y -
				rowLayouts[0].y +
				fontSize * 1.25 +
				labelPadY * 2,
		}
		const overlapsRect =
			backdrop.y < rect.y + rect.height && backdrop.y + backdrop.height > rect.y
		if (!overlapsRect) return backdrop
		const gap = fontSize * 0.35
		if (side === 'right') {
			const minX = rect.x + rect.width + strokeWidth / 2 + gap
			if (backdrop.x >= minX) return backdrop
			return {
				...backdrop,
				x: minX,
				width: Math.max(backdrop.width - (minX - backdrop.x), 0),
			}
		}
		const maxRight = rect.x - strokeWidth / 2 - gap
		const right = backdrop.x + backdrop.width
		if (right <= maxRight) return backdrop
		return {
			...backdrop,
			width: Math.max(maxRight - backdrop.x, 0),
		}
	})()
	const frameScale = Math.min(
		viewport.width / FRAME_WIDTH,
		viewport.height / FRAME_HEIGHT,
	)
	const frameOffsetX = (viewport.width - FRAME_WIDTH * frameScale) / 2
	const frameOffsetY = (viewport.height - FRAME_HEIGHT * frameScale) / 2

	return (
		<div
			ref={overlayRef}
			style={{
				position: 'absolute',
				top: IFRAME_BORDER_PX,
				left: IFRAME_BORDER_PX,
				width: `calc(100% - ${IFRAME_BORDER_PX * 2}px)`,
				height: `calc(100% - ${IFRAME_BORDER_PX * 2 + PLAYER_CONTROLS_HEIGHT_PX}px)`,
				pointerEvents: 'none',
			}}
		>
			{labelBackdrop && frameScale > 0 && (
				<div
					style={{
						position: 'absolute',
						left: frameOffsetX + labelBackdrop.x * frameScale,
						top: frameOffsetY + labelBackdrop.y * frameScale,
						width: labelBackdrop.width * frameScale,
						height: labelBackdrop.height * frameScale,
						borderRadius: 12,
						overflow: 'hidden',
						background:
							'linear-gradient(180deg, rgba(255,255,255,0.22), rgba(255,255,255,0.08))',
						backdropFilter: 'blur(18px) saturate(1.45)',
						WebkitBackdropFilter: 'blur(18px) saturate(1.45)',
					}}
				/>
			)}
			<svg
				viewBox={`0 0 ${FRAME_WIDTH} ${FRAME_HEIGHT}`}
				preserveAspectRatio='xMidYMid meet'
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
				}}
			>
				<rect
					x={rect.x}
					y={rect.y}
					width={rect.width}
					height={rect.height}
					rx={frameRadius}
					ry={frameRadius}
					fill='none'
					stroke={COLORS.main}
					strokeWidth={strokeWidth}
				/>
				<defs>
					<filter
						id={textShadowId}
						x='-20%'
						y='-20%'
						width='140%'
						height='140%'
					>
						<feDropShadow
							dx='0'
							dy='1.5'
							stdDeviation='4'
							floodColor='#000'
							floodOpacity='0.45'
						/>
					</filter>
				</defs>
				{rowLayouts.map(({ event, y, iconCx, textX }, index) => {
					const isMissed =
						event.status === 'skipped' || isConnectingProblem(event)
					const color = isMissed ? COLORS.error : COLORS.white
					const iconCy = y - fontSize * 0.35
					const mark = iconRadius * 0.45

					return (
						<g key={`${event.type}-${index}`}>
							<text
								ref={node => {
									textRefs.current[index] = node
								}}
								x={textX}
								y={y}
								textAnchor={side === 'right' ? 'start' : 'end'}
								fontSize={fontSize}
								fontWeight={600}
								fill={color}
								filter={isMissed ? undefined : `url(#${textShadowId})`}
							>
								{formatEvent(
									event,
									eventSeconds(event),
									isConnectingProblem(event),
								)}
							</text>
							<circle
								cx={iconCx}
								cy={iconCy}
								r={iconRadius}
								fill={isMissed ? COLORS.error : COLORS.success}
							/>
							<path
								d={
									isMissed
										? `M ${iconCx - mark} ${iconCy - mark} L ${iconCx + mark} ${iconCy + mark} M ${iconCx + mark} ${iconCy - mark} L ${iconCx - mark} ${iconCy + mark}`
										: `M ${iconCx - mark} ${iconCy} L ${iconCx - mark * 0.25} ${iconCy + mark * 0.75} L ${iconCx + mark} ${iconCy - mark * 0.7}`
								}
								fill='none'
								stroke={COLORS.white}
								strokeWidth={iconRadius * 0.28}
								strokeLinecap='round'
								strokeLinejoin='round'
							/>
						</g>
					)
				})}
			</svg>
		</div>
	)
}
